import React, { useState } from 'react';
import { Download, Trash2, Eye, EyeOff } from 'lucide-react';
import { errorLogger } from '../services/errorLogger';

const ErrorLogViewer: React.FC = () => {
  const [logs, setLogs] = useState(errorLogger.getLogs());
  const [showDetails, setShowDetails] = useState(false);

  const refreshLogs = () => {
    setLogs(errorLogger.getLogs());
  };

  const clearLogs = () => {
    errorLogger.clearLogs();
    setLogs([]);
  };

  const downloadLogs = () => {
    errorLogger.downloadLogs();
  };

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'error':
        return 'text-red-400 bg-red-400/10';
      case 'warning':
        return 'text-yellow-400 bg-yellow-400/10';
      case 'info':
        return 'text-blue-400 bg-blue-400/10';
      default:
        return 'text-gray-400 bg-gray-400/10';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('pt-BR');
  };

  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <div className="bg-dark-800 border border-dark-600 rounded-lg shadow-2xl w-96 max-h-96 overflow-hidden">
        <div className="bg-dark-700 px-4 py-3 border-b border-dark-600 flex items-center justify-between">
          <h3 className="text-white font-semibold">Error Logs ({logs.length})</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="text-gray-400 hover:text-white transition-colors"
              title={showDetails ? 'Ocultar detalhes' : 'Mostrar detalhes'}
            >
              {showDetails ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
            <button
              onClick={refreshLogs}
              className="text-gray-400 hover:text-white transition-colors"
              title="Atualizar logs"
            >
              ↻
            </button>
            <button
              onClick={downloadLogs}
              className="text-gray-400 hover:text-white transition-colors"
              title="Baixar logs"
            >
              <Download className="w-4 h-4" />
            </button>
            <button
              onClick={clearLogs}
              className="text-red-400 hover:text-red-300 transition-colors"
              title="Limpar logs"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        <div className="max-h-80 overflow-y-auto">
          {logs.length === 0 ? (
            <div className="p-4 text-center text-gray-400">
              Nenhum log encontrado
            </div>
          ) : (
            <div className="p-2 space-y-2">
              {logs.slice().reverse().map((log, index) => (
                <div
                  key={index}
                  className="bg-dark-700 rounded-lg p-3 border border-dark-600"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getLevelColor(log.level)}`}>
                        {log.level.toUpperCase()}
                      </span>
                      <span className="text-xs text-gray-400">
                        {formatTimestamp(log.timestamp)}
                      </span>
                    </div>
                  </div>
                  
                  <div className="text-sm text-white mb-2">
                    {log.message}
                  </div>
                  
                  {showDetails && (
                    <div className="space-y-2 text-xs">
                      {log.url && (
                        <div className="text-gray-400">
                          <strong>URL:</strong> {log.url}
                        </div>
                      )}
                      {log.userId && (
                        <div className="text-gray-400">
                          <strong>User:</strong> {log.userId} ({log.userType})
                        </div>
                      )}
                      {log.details && (
                        <details className="text-gray-400">
                          <summary className="cursor-pointer hover:text-gray-300">
                            Detalhes
                          </summary>
                          <pre className="mt-2 p-2 bg-dark-800 rounded text-xs overflow-x-auto">
                            {JSON.stringify(log.details, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ErrorLogViewer;
